'use client';

import { c as _c } from "react/compiler-runtime";
import useSWR from 'swr';
import { useSession } from 'next-auth/react';
import getCustomerAction from '@/app/_lib/actions/GetCustomerAction';
import getCustomerShippingAddressesAction from '@/app/_lib/actions/GetCustomerShippingAddressesAction';

// Stable empty array reference to prevent infinite re-renders
const EMPTY_ARRAY = [];
const customerFetcher = async () => {
  const response = await getCustomerAction();
  if (response.success) {
    return response.customer;
  }
  return null;
};
const addressesFetcher = async () => {
  const response = await getCustomerShippingAddressesAction();
  if (response.success) {
    return response.addresses;
  }
  return [];
};

/**
 * useCustomer - SWR-based customer profile hook
 *
 * Only fetches when user is authenticated.
 * Automatically refetches when auth status changes.
 *
 * Returns:
 *   customer: object - profile data (name, email, billing address, etc.)
 *   isLoading: boolean - true during initial fetch
 *   refresh: function - call after profile updates
 */
export function useCustomer() {
  const $ = _c(8);
  const {
    status: sessionStatus
  } = useSession();
  const isAuthenticated = sessionStatus === "authenticated";
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== sessionStatus) {
    t0 = isAuthenticated ? ["customer", sessionStatus] : null;
    $[0] = isAuthenticated;
    $[1] = sessionStatus;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: customer,
    isLoading,
    mutate
  } = useSWR(t0, customerFetcher, t1);
  const t2 = customer || null;
  const t3 = sessionStatus === "loading" || isAuthenticated && isLoading;
  let t4;
  if ($[4] !== mutate || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      customer: t2,
      isLoading: t3,
      refresh: mutate
    };
    $[4] = mutate;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/**
 * useShippingAddresses - SWR-based shipping addresses hook
 *
 * Only fetches when user is authenticated.
 *
 * Returns:
 *   addresses: array - shipping addresses
 *   isLoading: boolean - true during initial fetch
 *   refresh: function - call after address add/edit/delete
 */
export function useShippingAddresses() {
  const $ = _c(8);
  const {
    status: sessionStatus
  } = useSession();
  const isAuthenticated = sessionStatus === "authenticated";
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== sessionStatus) {
    t0 = isAuthenticated ? ["shippingAddresses", sessionStatus] : null;
    $[0] = isAuthenticated;
    $[1] = sessionStatus;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: addresses,
    isLoading,
    mutate
  } = useSWR(t0, addressesFetcher, t1);
  const t2 = addresses || EMPTY_ARRAY;
  const t3 = sessionStatus === "loading" || isAuthenticated && isLoading;
  let t4;
  if ($[4] !== mutate || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      addresses: t2,
      isLoading: t3,
      refresh: mutate
    };
    $[4] = mutate;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}